/**
 * المرحلة 1.4 — the visit's six policies and five switches as the screens
 * read them: the same cleaning as Backend/lib/clinicalPolicy.js, applied to
 * what /api/auth/capabilities sends as `locale.clinical`.
 *
 * The switches are the Super Admin's per institution (rule 13 of the master
 * reference): `encounters`, `icd10Coding`, `medicalCertificates`,
 * `externalReferrals`, `visitSummaries` — open, limited, trial, readonly,
 * closed or draining. A session outside an institution reads every switch
 * open and the platform's policies.
 *
 * A rendering aid, not the boundary: every route checks again.
 * Tests/suites/frontend/p14-prints.test.mjs keeps the two sides equal.
 */

export const SOAP_SECTIONS = Object.freeze(['s', 'o', 'a', 'p']);
export const VITAL_GROUPS = Object.freeze(['bp', 'hr', 'temp', 'spo2', 'rr', 'glucose', 'weight', 'height']);
export const CERTIFICATE_KINDS = Object.freeze(['sick_leave', 'fit_to_work', 'report']);
export const REFERRAL_KINDS = Object.freeze(['lab', 'imaging', 'hospital', 'clinician']);
export const SUMMARY_SECTIONS = Object.freeze(['subjective', 'objective', 'assessment', 'plan', 'vitals']);

const pick = (value, allowed, dflt) => (allowed.includes(String(value)) ? String(value) : dflt);
const num = (value, dflt, min, max) => {
  const n = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(n)) return dflt;
  return Math.min(max, Math.max(min, Math.round(n)));
};
const listOf = (value, allowed, dflt) => (Array.isArray(value) ? allowed.filter((v) => value.includes(v)) : [...dflt]);

function clinicalNote(p = {}) {
  return {
    format: pick(p.format, ['both', 'soap', 'free'], 'both'),
    required: listOf(p.required, SOAP_SECTIONS, []),
  };
}

function icd10(p = {}) {
  return {
    mode: pick(p.mode, ['optional', 'required', 'off'], 'optional'),
    onReport: pick(p.onReport, ['code', 'text'], 'code'),
    editors: pick(p.editors, ['managers', 'managers_and_doctors'], 'managers'),
  };
}

function visitVitals(p = {}) {
  return {
    required: listOf(p.required, VITAL_GROUPS, []),
    alerts: pick(p.alerts, ['standard', 'off'], 'standard'),
  };
}

function certificates(p = {}) {
  return {
    kinds: listOf(p.kinds, CERTIFICATE_KINDS, CERTIFICATE_KINDS),
    maxDays: num(p.maxDays, 14, 1, 90),
    approval: pick(p.approval, ['none', 'manager'], 'none'),
  };
}

function referrals(p = {}) {
  return {
    kinds: listOf(p.kinds, REFERRAL_KINDS, REFERRAL_KINDS),
    directory: pick(p.directory, ['both', 'own', 'platform'], 'both'),
  };
}

function patientVisitSummary(p = {}) {
  return {
    release: pick(p.release, ['doctor', 'auto', 'off'], 'doctor'),
    sections: listOf(p.sections, SUMMARY_SECTIONS, ['subjective', 'assessment', 'plan', 'vitals']),
    report: pick(p.report, ['request', 'download'], 'request'),
  };
}

export const READERS = Object.freeze({ clinicalNote, icd10, visitVitals, certificates, referrals, patientVisitSummary });
export const IDS = Object.freeze(Object.keys(READERS));

export const SWITCHES = Object.freeze({
  encounters: 'clinicalNote',
  icd10Coding: 'icd10',
  medicalCertificates: 'certificates',
  externalReferrals: 'referrals',
  visitSummaries: 'patientVisitSummary',
});
export const SWITCH_IDS = Object.freeze(Object.keys(SWITCHES));
const STATES = ['open', 'limited', 'trial', 'readonly', 'closed', 'draining'];

/** May new work be written under this switch? */
export const switchActive = (state) => ['open', 'limited', 'trial'].includes(state);
/** May what exists be read? */
export const switchReadable = (state) => state !== 'closed';

export const codingSystemFor = (countryCode) => (String(countryCode || '').toUpperCase() === 'SA' ? 'ICD-10-AM' : 'ICD-10');

/** The switches as sent, each a known state (missing: open). */
export function switchesOf(raw = {}) {
  const out = {};
  for (const id of SWITCH_IDS) out[id] = STATES.includes(raw && raw[id]) ? raw[id] : 'open';
  return out;
}

/** The policies as the switches leave them for new work (Backend/lib/clinicalPolicy.js effective). */
export function effective(policies = {}) {
  const sw = policies.switches || {};
  const out = { ...policies };
  if (!switchActive(sw.icd10Coding || 'open')) out.icd10 = { ...(policies.icd10 || icd10()), mode: 'off' };
  if (!switchActive(sw.medicalCertificates || 'open')) out.certificates = { ...(policies.certificates || certificates()), kinds: [] };
  if (!switchActive(sw.externalReferrals || 'open')) out.referrals = { ...(policies.referrals || referrals()), kinds: [] };
  if (!switchReadable(sw.visitSummaries || 'open')) out.patientVisitSummary = { ...(policies.patientVisitSummary || patientVisitSummary()), release: 'off' };
  return out;
}

/** Everything, cleaned, from `usePermissions().locale` (or null). */
export function clinicalPolicy(locale) {
  const raw = (locale && locale.clinical) || {};
  const out = {};
  for (const id of IDS) out[id] = READERS[id](raw[id] || {});
  out.codingSystem = raw.codingSystem === 'ICD-10-AM' ? 'ICD-10-AM' : 'ICD-10';
  out.switches = switchesOf(raw.switches || {});
  return out;
}

/** Is a switch on for new work in this session's institution? */
export const clinicalOpen = (locale, switchId) => switchActive(clinicalPolicy(locale).switches[switchId]);
/** Is a switch readable at all (anything but closed)? */
export const clinicalShown = (locale, switchId) => switchReadable(clinicalPolicy(locale).switches[switchId]);
